'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Breadcrumbs from '@/components/ui/breadcrumbs';
import { reviewPlatforms } from '@/lib/review-platforms';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const googleBusinessProfileUrl =
  'https://www.google.com/maps/place/OKTshirt/@52.5373889,-1.9131485,19.5z/data=!4m6!3m5!1s0x4870a30e8ca8ca13:0x1f935f32ecde8253!8m2!3d52.5377046!4d-1.9129045!16s%2Fg%2F11r97trw7y';

const googleBusinessEmbedUrl =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d428.9983988559986!2d-1.9131485!3d52.5373889!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4870a30e8ca8ca13%3A0x1f935f32ecde8253!2sOKTshirt!5e0!3m2!1sen!2suk!4v1';

const contactMethods = [
  {
    title: 'Phone',
    value: '020 4520 8521',
    href: 'tel:02045208521',
    description: 'Mon–Fri, 10:00–18:00',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
      />
    ),
  },
  {
    title: 'WhatsApp',
    value: '07403 343433',
    href: 'https://wa.me/447403343433',
    description: 'Quick responses via WhatsApp',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
      />
    ),
  },
  {
    title: 'Email',
    value: 'hello@oktshirt.co.uk',
    href: 'mailto:hello@oktshirt.co.uk',
    description: 'We reply within 24 hours',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
      />
    ),
  },
  {
    title: 'Visit Us',
    value: '126 Beeches Rd, Birmingham, B42 2HQ',
    href: googleBusinessProfileUrl,
    description: 'Great Barr, Birmingham',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
      />
    ),
  },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessType: 'personal',
    interests: [] as string[],
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const handleInterestChange = (interest: string) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter((i) => i !== interest)
        : [...prev.interests, interest],
    }));
  };

  const GAS_URL = 'https://script.google.com/macros/s/AKfycbwKQvAtPNjwpJmql7FXKu8bZ4ZHo_W4HnO6EjRVBvYiwZcrlUPkcONAwyOAkZPQv3xv/exec';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(false);

    try {
      // Send via hidden iframe (fire-and-forget, same pattern as Lapland Famille)
      const iframe = document.createElement('iframe');
      iframe.name = 'gas_notify_' + Date.now();
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const hiddenForm = document.createElement('form');
      hiddenForm.method = 'POST';
      hiddenForm.action = GAS_URL;
      hiddenForm.target = iframe.name;

      const fields: Record<string, string> = {
        form_type: 'contact',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        businessType: formData.businessType,
        interests: formData.interests.join(', '),
        message: formData.message,
      };

      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        hiddenForm.appendChild(input);
      });

      document.body.appendChild(hiddenForm);
      hiddenForm.submit();
      hiddenForm.remove();
      setTimeout(() => iframe.remove(), 10000);

      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Breadcrumbs items={[{ label: 'Contact' }]} />
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-black">
        <div className="container">
          <motion.div
            className="max-w-[48rem] mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-4">
              We&apos;d Love to Hear From You
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
              Contact Us
            </h1>
            <p className="text-lg lg:text-xl text-gray-300 leading-relaxed">
              Have a question, need a quote, or want to discuss a project? Get in touch and we&apos;ll get back to you as quickly as possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 lg:py-24">
        <div className="container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-[64rem] mx-auto">
            {contactMethods.map((method, i) => (
              <motion.a
                key={method.title}
                href={method.href}
                target={method.href.startsWith('http') ? '_blank' : undefined}
                rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="block p-8 bg-[var(--color-off-white)] border border-[var(--color-border)] text-center hover:border-black transition-colors"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, ease: 'easeOut', delay: i * 0.1 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-white">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8"
                  >
                    {method.icon}
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-1">{method.title}</h3>
                <p className="text-sm font-medium mb-1">{method.value}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{method.description}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Review Platforms */}
      <section className="border-y border-[var(--color-border)] bg-white py-12 lg:py-16">
        <div className="container">
          <motion.div
            className="grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                Customer reviews
              </p>
              <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">
                Review OKTshirt or check our profiles.
              </h2>
              <p className="mt-4 text-sm leading-6 text-[var(--color-text-secondary)]">
                These links open the main third-party profiles customers use when checking or leaving feedback.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {reviewPlatforms.map((platform) => (
                <a
                  key={platform.name}
                  href={platform.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group border border-[var(--color-border)] bg-[var(--color-off-white)] p-5 transition-colors hover:border-black hover:bg-white"
                  aria-label={`${platform.cta} in a new tab`}
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                    {platform.label}
                  </span>
                  <h3 className="mt-3 text-base font-bold text-black">{platform.name}</h3>
                  <span className="mt-4 inline-flex items-center text-sm font-semibold text-black">
                    {platform.cta}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </span>
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form + Map */}
      <section className="py-16 lg:py-24 bg-[var(--color-off-white)]">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 max-w-[64rem] mx-auto">
            {/* Form */}
            <motion.div {...fadeUp}>
              <h2 className="text-2xl lg:text-3xl font-bold mb-6">
                Send Us a Message
              </h2>
              {submitted ? (
                <div className="p-8 bg-white border border-[var(--color-border)] text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-green-50 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-8 h-8 text-green-600"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Message Sent</h3>
                  <p className="text-[var(--color-text-secondary)]">
                    Thank you for getting in touch. We&apos;ll reply within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <p className="block text-sm font-medium mb-2">This is for:</p>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="businessType"
                          value="personal"
                          checked={formData.businessType === 'personal'}
                          onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                          className="accent-black"
                        />
                        Personal
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="businessType"
                          value="company"
                          checked={formData.businessType === 'company'}
                          onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                          className="accent-black"
                        />
                        Company
                      </label>
                    </div>
                  </div>

                  <div>
                    <p className="block text-sm font-medium mb-2">Interested in:</p>
                    <div className="flex flex-wrap gap-4">
                      {['T-Shirts', 'Hoodies', 'Workwear', 'School Uniforms', 'Banners', 'Other'].map(
                        (interest) => (
                          <label key={interest} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.interests.includes(interest)}
                              onChange={() => handleInterestChange(interest)}
                              className="accent-black"
                            />
                            {interest}
                          </label>
                        )
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 border border-[var(--color-border)] bg-white text-sm focus:outline-none focus:border-black transition-colors resize-vertical"
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-red-600">
                      Something went wrong. Please try again or email us directly at hello@oktshirt.co.uk.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center px-8 py-3 text-sm font-semibold bg-brand-blue text-white hover:bg-brand-blue-hover transition-colors uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Map + Info */}
            <div>
              <motion.div
                className="mb-8"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
              >
                <div className="aspect-[4/3] bg-[var(--color-border)]">
                  <iframe
                    src={googleBusinessEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="OK T-Shirt location"
                  />
                </div>
              </motion.div>

              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
              >
                <div>
                  <h3 className="font-semibold mb-2">Opening Hours</h3>
                  <div className="text-sm text-[var(--color-text-secondary)] space-y-1">
                    <p>Monday – Friday: 10:00 – 18:00</p>
                    <p>Saturday: By appointment</p>
                    <p>Sunday: Closed</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Service Areas</h3>
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    Birmingham, Dudley, Erdington, Tamworth, Walsall, Wolverhampton, Solihull, Sutton Coldfield, Smethwick, West Bromwich, Coventry, and Worcester.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-black">
        <div className="container">
          <motion.div
            className="max-w-[64rem] mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight leading-tight text-white">
              Need It Fast?
            </h2>
            <p className="text-lg lg:text-xl text-gray-300 mb-10 leading-relaxed">
              Call us directly or send a WhatsApp message for the quickest response.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:02045208521"
                className="group inline-flex items-center justify-center gap-2 px-10 py-4 text-base font-semibold bg-white text-black hover:bg-gray-100 transition-all duration-300"
              >
                Call Now
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </a>
              <a
                href="https://wa.me/447403343433"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-4 text-base font-semibold border border-white text-white hover:bg-white hover:text-black transition-all duration-300"
              >
                WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
